import { useMemo, useState } from 'react'
import { useTranslation } from '@kostbar/i18n'
import { AlertCircle, Loader2, Printer, RefreshCw } from 'lucide-react'
import { usePickupsQuery } from '../../api/use-pickups-query'
import { formatCurrency } from '../../lib/format'
import {
  PICKUP_STATUS_BADGES,
  UNSETTLED_LABEL,
  customerLabel,
  formatGeneratedAt,
  formatPickupTime,
  formatReportDate,
  paymentSummary,
  pickupCodeLabel,
  temperatureSuffix,
  todayValue,
  tomorrowValue,
} from './pickup-config'
import type { PickupOrderRow, PickupReport } from '../../api/use-pickups-query'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { useRestaurants } from '@/features/products/hooks/use-restaurants'

const ALL_RESTAURANTS = 'all'

export function PickupReportPage() {
  const { t } = useTranslation('reports')
  const { data: restaurantsData } = useRestaurants()
  const restaurants = restaurantsData?.items ?? []

  const [date, setDate] = useState(todayValue())
  // Null means "not chosen yet", which is what lets the default below apply without overriding a
  // deliberate pick — a user of a single-restaurant tenant can still select all-restaurants mode.
  const [restaurantValue, setRestaurantValue] = useState<string | null>(null)

  // A single-restaurant tenant has no meaningful choice to make on arrival, so make it for them.
  const effectiveRestaurantValue =
    restaurantValue ??
    (restaurants.length === 1 ? String(restaurants[0].id) : ALL_RESTAURANTS)

  const restaurantId =
    effectiveRestaurantValue === ALL_RESTAURANTS
      ? null
      : Number(effectiveRestaurantValue)

  const { data, isLoading, isFetching, isError, error, refetch } =
    usePickupsQuery(date, restaurantId)

  const allRestaurantsMode = restaurantId == null

  return (
    <PermissionGate action={ACTIONS.TRANSACTION_VIEW}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-end gap-3" data-print-hide>
          <div className="space-y-1">
            <label
              className="text-sm text-muted-foreground"
              htmlFor="pickup-date"
            >
              {t('pickups.date')}
            </label>
            <Input
              id="pickup-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-44"
            />
          </div>

          <Button variant="outline" onClick={() => setDate(todayValue())}>
            {t('pickups.today')}
          </Button>
          <Button variant="outline" onClick={() => setDate(tomorrowValue())}>
            {t('pickups.tomorrow')}
          </Button>

          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">
              {t('filters.location')}
            </label>
            <Select
              value={effectiveRestaurantValue}
              onValueChange={setRestaurantValue}
            >
              <SelectTrigger className="w-64">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_RESTAURANTS}>
                  {t('pickups.allRestaurants')}
                </SelectItem>
                {restaurants.map((restaurant) => (
                  <SelectItem key={restaurant.id} value={String(restaurant.id)}>
                    {restaurant.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="ml-auto flex gap-2">
            <Button
              variant="outline"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              {t('pickups.refresh')}
            </Button>
            <Button onClick={() => window.print()} disabled={!data}>
              <Printer className="h-4 w-4" />
              {t('pickups.print')}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-96" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : isError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('pickups.loadFailed')}</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : null}
            </AlertDescription>
          </Alert>
        ) : data ? (
          <ReportBody
            report={data}
            allRestaurantsMode={allRestaurantsMode}
            t={t}
          />
        ) : null}
      </div>
    </PermissionGate>
  )
}

interface ReportBodyProps {
  report: PickupReport
  allRestaurantsMode: boolean
  t: (key: string, options?: Record<string, unknown>) => string
}

function ReportBody({ report, allRestaurantsMode, t }: ReportBodyProps) {
  const heading = report.restaurant
    ? t('pickups.headingRestaurant', { name: report.restaurant.name })
    : t('pickups.headingAll')

  return (
    <div className="space-y-6">
      <header className="space-y-2 border-b border-border pb-4">
        <h1 className="text-2xl font-bold">{heading}</h1>
        <p className="text-sm text-muted-foreground">
          {t('pickups.dateLine', { date: formatReportDate(report.date) })}
          {report.restaurant
            ? ` · ${t('pickups.locationLine', {
                id: report.restaurant.id,
                code: report.restaurant.code,
              })}`
            : null}
        </p>
        <p className="text-sm text-muted-foreground">
          {t('pickups.generatedAt', {
            stamp: formatGeneratedAt(report.generatedAt, report.timezone),
            timezone: report.timezone,
          })}
        </p>
        <p className="font-medium">
          {t('pickups.summary', {
            orders: report.totals.orderCount,
            dishes: report.totals.dishCount,
            gross: formatCurrency(report.totals.totalGrossCents),
          })}
        </p>
        {/* On paper the reader cannot refresh, so the sheet has to say what it cannot know. */}
        <p className="hidden text-sm text-muted-foreground print:block">
          {t('pickups.snapshotNote')}
        </p>
      </header>

      {report.truncated ? (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('pickups.truncatedTitle')}</AlertTitle>
          <AlertDescription>{t('pickups.truncatedBody')}</AlertDescription>
        </Alert>
      ) : null}

      {report.orders.length === 0 ? (
        <div className="rounded-md border border-border p-8 text-center text-muted-foreground">
          {t('pickups.empty')}
        </div>
      ) : (
        <>
          <DishTotals report={report} t={t} />
          <OrderTable
            report={report}
            allRestaurantsMode={allRestaurantsMode}
            t={t}
          />
        </>
      )}
    </div>
  )
}

function DishTotals({
  report,
  t,
}: Omit<ReportBodyProps, 'allRestaurantsMode'>) {
  return (
    <section className="space-y-2 break-inside-avoid">
      <h2 className="text-lg font-semibold">{t('pickups.production')}</h2>
      <ul className="grid gap-1 sm:grid-cols-2 lg:grid-cols-3">
        {report.dishTotals.map((dish) => (
          <li
            key={`${dish.name}:${dish.temperature ?? '-'}`}
            className="flex gap-2 text-sm"
          >
            <span className="font-semibold tabular-nums">{dish.quantity}×</span>
            <span>
              {dish.name}
              {temperatureSuffix(dish.temperature)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  )
}

function OrderTable({ report, allRestaurantsMode, t }: ReportBodyProps) {
  const orders = useMemo(() => report.orders, [report.orders])

  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">{t('pickups.orders')}</h2>
      {/* data-print-fit: the sideways scroll is a screen affordance; on paper the table must reflow. */}
      <div className="overflow-x-auto" data-print-fit>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16" data-print-tight>
                {t('pickups.column.time')}
              </TableHead>
              {allRestaurantsMode ? (
                <TableHead data-print-tight>{t('filters.location')}</TableHead>
              ) : null}
              <TableHead className="w-24" data-print-tight>
                {t('pickups.column.code')}
              </TableHead>
              <TableHead>{t('pickups.column.name')}</TableHead>
              <TableHead>{t('pickups.column.items')}</TableHead>
              <TableHead>{t('pickups.column.payment')}</TableHead>
              <TableHead data-print-tight>
                {t('pickups.column.status')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <OrderRow
                key={order.orderId}
                order={order}
                timezone={report.timezone}
                allRestaurantsMode={allRestaurantsMode}
              />
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  )
}

interface OrderRowProps {
  order: PickupOrderRow
  timezone: string
  allRestaurantsMode: boolean
}

function OrderRow({ order, timezone, allRestaurantsMode }: OrderRowProps) {
  const status = PICKUP_STATUS_BADGES[order.status]

  return (
    <TableRow className="break-inside-avoid align-top">
      <TableCell className="font-medium tabular-nums" data-print-tight>
        {formatPickupTime(order.pickupAt, timezone)}
      </TableCell>
      {allRestaurantsMode ? (
        <TableCell data-print-tight>{order.restaurantName}</TableCell>
      ) : null}
      <TableCell className="font-mono font-semibold" data-print-tight>
        {pickupCodeLabel(order)}
      </TableCell>
      <TableCell>{customerLabel(order)}</TableCell>
      <TableCell>
        <ul className="space-y-0.5">
          {order.items.map((item, index) => (
            <li key={`${order.orderId}-${index}`} className="flex gap-2">
              <span className="tabular-nums text-muted-foreground">
                {item.quantity}×
              </span>
              <span>
                {item.name}
                {temperatureSuffix(item.temperature)}
              </span>
            </li>
          ))}
        </ul>
      </TableCell>
      <TableCell>
        <span>{paymentSummary(order)}</span>
        {order.settled ? null : (
          <Badge variant="destructive" className="ml-2 align-middle">
            {UNSETTLED_LABEL}
          </Badge>
        )}
      </TableCell>
      <TableCell data-print-tight>
        <Badge variant={status?.variant ?? 'outline'}>
          {status?.label ?? order.status}
        </Badge>
      </TableCell>
    </TableRow>
  )
}
